/**
 * Where the money goes.
 *
 * Tim, 2026-08-16: buy-ins have been **$90** since about 2019, and the pot pays **$100 to the
 * regular-season high score**, **$90 to second**, and **everything left to the champion** —
 * including any real money that came into the pot during the season.
 *
 * Three things that structure has to respect, all of them in the rulebook:
 *
 *   - "Regular-season high score" means the REGULAR season. `matchup-history.json` marks every
 *     game `regular` or playoff and states the week count, so this is summed from the games
 *     rather than read off the standings, whose PF includes playoff weeks. Using the standings
 *     column would hand the prize to whoever had the deepest run in some seasons.
 *   - Real money reaches the pot two ways: buying FAAB at $2 real per ₣1 (2020-I-6), and — in
 *     2019 only — paying cash instead of FAAB for a 6th or 7th keeper (2019-III-3 §2-4, which
 *     2020-III-1 repealed). Both go to the champion.
 *   - Those additions are NOT recorded for past seasons. The app has a FAAB ledger for the
 *     current season and nothing before it, so a past champion's line is a floor, not a total,
 *     and says so rather than quietly understating what somebody won.
 *
 * Before 2019 the league's own rules record that dues exist (2015-I-3) but never state an
 * amount, so those seasons have no payout at all here. Blank beats invented.
 */

import standings from '../data/standings-history.json';
import matchups from '../data/matchup-history.json';
import keeperHistory from '../data/keeper-history.json';
import { gmFor, identityFor } from './identity.js';

/** The structure in force since 2019, as Tim states it. One place to change it. */
export const PAYOUTS = {
  from: 2019,
  buyIn: 90,
  highScore: 100,
  runnerUp: 90,
} as const;

interface StandingRow {
  rank: number; team: string; gm: string | null; w: number; l: number; t: number;
  pf: number; pa: number; gmResolved?: boolean;
}
interface SeasonStandings { season: number; teams: number; standings: StandingRow[] }
interface Side { gm: string | null; team: string; points: number }
interface Matchup { week: number; kind: string; home: Side; away: Side }
interface SeasonMatchups { season: number; regularSeasonWeeks?: number; matchups: Matchup[] }
interface KeeperSeason { season: number; keepers: Array<{ gm: string | null }> }

const STANDINGS = (standings as unknown as { seasons: SeasonStandings[] }).seasons;
const MATCHUPS = (matchups as unknown as { seasons: SeasonMatchups[] }).seasons;
const KEEPERS = (keeperHistory as unknown as { seasons: KeeperSeason[] }).seasons;

export const payoutSeasons = (): number[] => STANDINGS
  .map((s) => s.season)
  .filter((y) => y >= PAYOUTS.from)
  .sort((a, b) => b - a);

/* -- the regular-season points title ------------------------------------------------------- */

export interface HighScore { gm: string; points: number }

/**
 * Most regular-season points, from the games themselves.
 *
 * Returns null where the season's matchups are not in the archive at all — a prize nobody can
 * point at a winner for is not a prize the screen should name.
 */
export function regularSeasonHighScore(season: number): HighScore | null {
  const file = MATCHUPS.find((s) => s.season === season);
  if (!file) return null;
  const points = new Map<string, number>();
  for (const m of file.matchups) {
    if (m.kind !== 'regular') continue;
    for (const side of [m.home, m.away]) {
      const gm = side.gm ?? gmFor(season, { gm: side.gm, team: side.team });
      if (!gm) continue;
      points.set(gm, (points.get(gm) ?? 0) + side.points);
    }
  }
  const best = [...points.entries()].sort((a, b) => b[1] - a[1])[0];
  return best ? { gm: best[0], points: Math.round(best[1] * 100) / 100 } : null;
}

/* -- the toilet bowl ----------------------------------------------------------------------- */

export interface ToiletBowl { gm: string; week: number }

/**
 * Who won the consolation bracket, worked out from the games rather than from where Yahoo put
 * people in the final table.
 *
 * The bracket final is the last-week consolation game between two teams that each won a
 * consolation game the week before — the other games that week are placement matches between
 * teams already knocked out of it. That resolves cleanly for every season the archive has
 * consolation games for (2010 on) and returns null for the ones it does not (2009 and earlier),
 * rather than falling back to "whoever finished last", which is a different thing entirely.
 *
 * A tied final is settled the way the league settles one — Yahoo's Playoff Tie-Breaker setting,
 * "best regular season record vs opponent wins" — which is why 2015's dead heat between Rajan
 * and Amit reads as Rajan: he took their week 9 meeting.
 *
 * Worth noting: `season-2025.json` carries an INFERRED toilet-bowl winner, flagged unconfirmed,
 * read off the standings order. This derivation reaches the same name from the scores.
 */
export function toiletBowlWinner(season: number): ToiletBowl | null {
  const file = MATCHUPS.find((s) => s.season === season);
  if (!file) return null;
  const games = file.matchups.filter((m) => m.kind === 'consolation');
  if (games.length === 0) return null;

  const weeks = [...new Set(games.map((g) => g.week))].sort((a, b) => a - b);
  const lastWeek = weeks[weeks.length - 1]!;
  const earlierWinners = new Set(
    games.filter((g) => g.week < lastWeek).map((g) => winnerOf(g, season)).filter(Boolean) as string[],
  );

  const finals = games.filter((g) => g.week === lastWeek);
  const bracketFinal = finals.filter((g) => {
    const home = g.home.gm ?? gmFor(season, g.home);
    const away = g.away.gm ?? gmFor(season, g.away);
    return home && away && earlierWinners.has(home) && earlierWinners.has(away);
  });
  // One game, or nothing. An ambiguous bracket is not a result worth publishing.
  if (bracketFinal.length !== 1) return null;
  const won = winnerOf(bracketFinal[0]!, season);
  return won ? { gm: won, week: lastWeek } : null;
}

/**
 * The archive names a winner on most games; where it does not, the scores do — and where the
 * scores are level, the league's own tie-breaker does.
 *
 * Yahoo's setting is "Playoff Tie-Breaker: best regular season record vs opponent wins", so a
 * tied postseason game goes to whoever won more of that season's regular-season meetings. A
 * split series or no meeting at all leaves it genuinely unresolved, and the app says so.
 */
function winnerOf(g: Matchup, season: number): string | null {
  const named = (g as unknown as { winner?: string | null }).winner;
  if (named) return named;
  const home = g.home.gm;
  const away = g.away.gm;
  if (g.home.points !== g.away.points) {
    return (g.home.points > g.away.points ? home : away) ?? null;
  }
  if (!home || !away) return null;
  return headToHeadEdge(season, home, away);
}

/** Who took the regular-season series between two GMs, or null if it was split. */
function headToHeadEdge(season: number, a: string, b: string): string | null {
  const file = MATCHUPS.find((s) => s.season === season);
  if (!file) return null;
  let aWins = 0;
  let bWins = 0;
  for (const m of file.matchups) {
    if (m.kind !== 'regular') continue;
    const gms = [m.home.gm, m.away.gm];
    if (!gms.includes(a) || !gms.includes(b)) continue;
    const winner = (m as unknown as { winner?: string | null }).winner
      ?? (m.home.points === m.away.points
        ? null
        : (m.home.points > m.away.points ? m.home.gm : m.away.gm));
    if (winner === a) aWins += 1;
    if (winner === b) bWins += 1;
  }
  if (aWins === bWins) return null;
  return aWins > bWins ? a : b;
}

/* -- a season's money ---------------------------------------------------------------------- */

export interface PayoutLine {
  place: 'champion' | 'runner-up' | 'high score';
  label: string;
  gm: string | null;
  amount: number;
  /** True where real-money additions are unrecorded, so the figure is a floor. */
  atLeast?: boolean;
  /** The most it could have been, where the rules of that season bound it. */
  upTo?: number;
}

/**
 * The most real money the 2019 keeper rule could have added to that year's pot.
 *
 * 2019-III-3 let a GM pay $10 cash for a 6th keeper and $20 more for a 7th INSTEAD of the FAAB
 * luxury tax, with the cash going to the champion — so the choice was each GM's and was never
 * recorded. What the keeper archive can say is how many GMs were in a position to pay: nine kept
 * six or more that year and four kept seven, which bounds the pot rather than guessing at it.
 *
 * The window is 2019 alone. 2020-III-1 made the luxury tax pure FAAB again, and buying FAAB with
 * real money (2020-I-6) did not exist yet in 2019 — so 2019 is the one season whose pot has a
 * ceiling the app can actually compute.
 */
export function keeperCashCeiling(season: number): number | null {
  if (season !== 2019) return null;
  const file = KEEPERS.find((s) => s.season === season);
  if (!file) return null;
  const counts = new Map<string, number>();
  for (const k of file.keepers) {
    const gm = (k.gm ?? '').toUpperCase();
    if (!gm) continue;
    counts.set(gm, (counts.get(gm) ?? 0) + 1);
  }
  let ceiling = 0;
  for (const n of counts.values()) {
    if (n >= 6) ceiling += 10;
    if (n >= 7) ceiling += 20;
  }
  return ceiling;
}

export interface SeasonPayout {
  season: number;
  teams: number;
  pot: number;
  /** Real money added during the season, where the app can actually see it. */
  added: number;
  additionsKnown: boolean;
  lines: PayoutLine[];
}

/**
 * The three payouts for a season, or null before the structure existed.
 *
 * `added` is what the caller can prove — the current season's FAAB purchases, once there are
 * any. Everything else is buy-ins, so the champion's line is the pot minus the two fixed
 * prizes, and is marked `atLeast` whenever additions could exist but are not in the archive.
 */
export function payoutsFor(season: number, added = 0, additionsKnown = false): SeasonPayout | null {
  if (season < PAYOUTS.from) return null;
  const table = STANDINGS.find((s) => s.season === season);
  if (!table) return null;

  const teams = table.teams || table.standings.length;
  const pot = teams * PAYOUTS.buyIn + added;
  const championRow = table.standings.find((r) => r.rank === 1);
  const runnerUpRow = table.standings.find((r) => r.rank === 2);
  const high = regularSeasonHighScore(season);
  const first = pot - PAYOUTS.highScore - PAYOUTS.runnerUp;
  // Only 2019 has a computable upper bound; everywhere else the additions are open-ended.
  const ceiling = additionsKnown ? 0 : keeperCashCeiling(season) ?? 0;

  return {
    season,
    teams,
    pot,
    added,
    additionsKnown,
    lines: [
      {
        place: 'champion',
        label: 'Champion',
        gm: championRow ? gmFor(season, championRow) : null,
        amount: first,
        atLeast: !additionsKnown,
        ...(ceiling ? { upTo: first + ceiling } : {}),
      },
      {
        place: 'runner-up',
        label: 'Runner-up',
        gm: runnerUpRow ? gmFor(season, runnerUpRow) : null,
        amount: PAYOUTS.runnerUp,
      },
      {
        place: 'high score',
        label: 'Regular-season high score',
        gm: high?.gm ?? null,
        amount: PAYOUTS.highScore,
      },
    ],
  };
}

/* -- what a manager has won ---------------------------------------------------------------- */

export interface ManagerMoney {
  gm: string;
  titles: number;
  finals: number;
  /** Seasons he actually played inside the payout era — what he paid in. */
  seasonsPaid: number;
  won: number;
  net: number;
}

export function managerMoney(gm: string): ManagerMoney {
  const me = gm.toUpperCase();
  let titles = 0;
  let finals = 0;
  let seasonsPaid = 0;
  let won = 0;

  for (const table of STANDINGS) {
    /*
     * `identityFor` rather than `gmFor`, so a standalone identity — an unpaired team carrying its
     * own name — can be asked about its own titles the same way a manager is.
     */
    const mine = table.standings.find((r) => identityFor(table.season, r).toUpperCase() === me);
    if (!mine) continue;
    if (mine.rank === 1) titles += 1;
    if (mine.rank <= 2) finals += 1;
    if (table.season < PAYOUTS.from) continue;
    seasonsPaid += 1;
    const money = payoutsFor(table.season);
    for (const line of money?.lines ?? []) {
      if ((line.gm ?? '').toUpperCase() === me) won += line.amount;
    }
  }

  return { gm: me, titles, finals, seasonsPaid, won, net: won - seasonsPaid * PAYOUTS.buyIn };
}

/** Every season's champion, newest first — the spine of the champions table. */
export function championsByYear(): Array<{ season: number; gm: string | null; team: string }> {
  return [...STANDINGS]
    .sort((a, b) => b.season - a.season)
    .map((s) => {
      const row = s.standings.find((r) => r.rank === 1);
      return { season: s.season, gm: row ? gmFor(s.season, row) : null, team: row?.team ?? '' };
    });
}

/**
 * How long since each manager last won, in seasons — the drawn "longest title drought" tile.
 *
 * A manager who has never won it counts from his first season, because "never" is a drought too
 * and leaving him out would hand the tile to somebody with a shorter wait.
 *
 * `among` narrows it to the current roster. Without it the answer is always a manager who left
 * the league twenty years ago, which is true and useless — the tile is about the people still
 * waiting.
 */
export function droughts(among?: readonly string[]): Array<{ gm: string; seasons: number; everWon: boolean }> {
  const roster = among ? new Set(among.map((g) => g.toUpperCase())) : null;
  const latest = Math.max(...STANDINGS.map((s) => s.season));
  const seen = new Map<string, { last: number | null; first: number }>();

  for (const table of [...STANDINGS].sort((a, b) => a.season - b.season)) {
    for (const row of table.standings) {
      const gm = gmFor(table.season, row);
      if (!gm) continue;
      const entry = seen.get(gm) ?? { last: null, first: table.season };
      if (row.rank === 1) entry.last = table.season;
      seen.set(gm, entry);
    }
  }

  return [...seen.entries()]
    .filter(([gm]) => !roster || roster.has(gm.toUpperCase()))
    .map(([gm, e]) => ({
      gm,
      seasons: latest - (e.last ?? e.first),
      everWon: e.last !== null,
    }))
    .sort((a, b) => b.seasons - a.seasons);
}
